/**
 * A ZIP writer, in the browser, because the server cannot make this archive.
 *
 * Bulk download used to be a worker's job: it read the files from storage and
 * zipped them. Encryption ended that — the worker holds no key, so it would
 * produce a real, openable zip containing a folder of files nobody can read,
 * which is the worst kind of failure because it looks like success. Exports
 * refuse sealed files outright today. The archive has to be assembled where the
 * keys are, and that is here.
 *
 * **Stored, not deflated.** Every entry goes in uncompressed. These are JPEGs,
 * WebPs and MP4s — already compressed, and deflating them again spends a second
 * per photograph to save a fraction of a percent. Store-only also means no
 * compression library, and the writer below is small enough to read.
 *
 * **No ZIP64.** The format's original fields are 32-bit, which caps an archive
 * at 4 GB and 65,535 entries. Rather than implement the extension, `addFile`
 * refuses past those limits with a message saying so — a truncated archive that
 * opens and is missing photographs is far worse than a refusal.
 */

/** The 4 GB ceiling of the classic format's 32-bit size fields. */
export const ZIP_MAX_BYTES = 0xffff_ffff;

/** The 16-bit entry count in the end-of-central-directory record. */
export const ZIP_MAX_ENTRIES = 0xffff;

export class ZipTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ZipTooLargeError";
  }
}

/*
 * CRC-32, table-driven. Required by the format for every entry — an archive
 * with wrong checksums opens in some tools and is rejected by others, which is
 * exactly the sort of half-working artefact worth avoiding.
 */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb8_8320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffff_ffff;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffff_ffff) >>> 0;
}

/** Somewhere for the archive to go: a file on disk, or memory. */
export interface ZipSink {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

interface Entry {
  nameBytes: Uint8Array;
  crc: number;
  size: number;
  offset: number;
  dosTime: number;
  dosDate: number;
}

export class ZipWriter {
  private readonly entries: Entry[] = [];
  private offset = 0;
  private readonly used = new Set<string>();

  constructor(private readonly sink: ZipSink) {}

  /**
   * Add one file, whole.
   *
   * The bytes are already in hand — they had to be decrypted to get here — so
   * there is nothing to gain from streaming a single entry, and having the
   * length up front is what lets the local header be written correctly instead
   * of needing a data descriptor after it.
   */
  async addFile(name: string, bytes: Uint8Array, modified?: Date): Promise<void> {
    if (this.entries.length >= ZIP_MAX_ENTRIES) {
      throw new ZipTooLargeError(
        `A zip can hold ${ZIP_MAX_ENTRIES.toLocaleString()} files. Select fewer and download in batches.`,
      );
    }

    if (this.offset + bytes.length > ZIP_MAX_BYTES) {
      throw new ZipTooLargeError(
        "This selection is over 4 GB, which is more than a zip file can hold. Select fewer and download in batches.",
      );
    }

    const nameBytes = new TextEncoder().encode(this.uniqueName(name));
    const crc = crc32(bytes);
    const { dosTime, dosDate } = dosStamp(modified ?? new Date());

    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x0403_4b50, true); // local file header
    view.setUint16(4, 20, true); // version needed: 2.0
    view.setUint16(6, 0x0800, true); // flags: names are UTF-8
    view.setUint16(8, 0, true); // method: stored
    view.setUint16(10, dosTime, true);
    view.setUint16(12, dosDate, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, bytes.length, true);
    view.setUint32(22, bytes.length, true);
    view.setUint16(26, nameBytes.length, true);
    view.setUint16(28, 0, true); // no extra field
    header.set(nameBytes, 30);

    const entry: Entry = {
      nameBytes,
      crc,
      size: bytes.length,
      offset: this.offset,
      dosTime,
      dosDate,
    };
    this.entries.push(entry);

    await this.sink.write(header);
    await this.sink.write(bytes);
    this.offset += header.length + bytes.length;
  }

  /** Write the central directory and finish. The archive is invalid until this. */
  async close(): Promise<void> {
    const start = this.offset;

    for (const entry of this.entries) {
      const record = new Uint8Array(46 + entry.nameBytes.length);
      const view = new DataView(record.buffer);
      view.setUint32(0, 0x0201_4b50, true); // central directory header
      view.setUint16(4, 20, true); // version made by
      view.setUint16(6, 20, true); // version needed
      view.setUint16(8, 0x0800, true); // UTF-8 names
      view.setUint16(10, 0, true); // stored
      view.setUint16(12, entry.dosTime, true);
      view.setUint16(14, entry.dosDate, true);
      view.setUint32(16, entry.crc, true);
      view.setUint32(20, entry.size, true);
      view.setUint32(24, entry.size, true);
      view.setUint16(28, entry.nameBytes.length, true);
      view.setUint16(30, 0, true); // extra
      view.setUint16(32, 0, true); // comment
      view.setUint16(34, 0, true); // disk number
      view.setUint16(36, 0, true); // internal attributes
      view.setUint32(38, 0, true); // external attributes
      view.setUint32(42, entry.offset, true);
      record.set(entry.nameBytes, 46);

      await this.sink.write(record);
      this.offset += record.length;
    }

    const end = new Uint8Array(22);
    const view = new DataView(end.buffer);
    view.setUint32(0, 0x0605_4b50, true); // end of central directory
    view.setUint16(4, 0, true); // this disk
    view.setUint16(6, 0, true); // disk with the directory
    view.setUint16(8, this.entries.length, true);
    view.setUint16(10, this.entries.length, true);
    view.setUint32(12, this.offset - start, true);
    view.setUint32(16, start, true);
    view.setUint16(20, 0, true); // no comment

    await this.sink.write(end);
    await this.sink.close();
  }

  /**
   * A name no other entry in this archive has.
   *
   * Two photographs called `IMG_0042.jpg` from different phones is the normal
   * case, not an edge one. Unzip tools handle the collision differently —
   * silently overwriting, in more than one of them — so the second copy becomes
   * `IMG_0042 (2).jpg` before it is ever written.
   */
  private uniqueName(name: string): string {
    const safe = sanitise(name);
    if (!this.used.has(safe)) {
      this.used.add(safe);
      return safe;
    }

    const dot = safe.lastIndexOf(".");
    const stem = dot > 0 ? safe.slice(0, dot) : safe;
    const extension = dot > 0 ? safe.slice(dot) : "";

    for (let n = 2; ; n += 1) {
      const candidate = `${stem} (${String(n)})${extension}`;
      if (!this.used.has(candidate)) {
        this.used.add(candidate);
        return candidate;
      }
    }
  }
}

/**
 * A filename that will survive being written to disk.
 *
 * Path separators are the one that matters for safety: a stored name of
 * `../../.bashrc` is the classic zip-slip, and while it is our own database
 * these names came from, an archive is opened by a tool that trusts it.
 */
function sanitise(name: string): string {
  const cleaned = name
    /*
     * Separators first, then the parent reference itself. Stripping only the
     * leading dots leaves `a/../b` as `a_.._b`, and more than one archive tool
     * re-splits stored names on the separators it finds.
     */
    .replace(/[/\\:*?"<>|]/g, "_")
    .replace(/\.{2,}/g, "_")
    .replace(/^[.\s]+/, "")
    .trim();
  return cleaned.length > 0 ? cleaned.slice(0, 200) : "photo";
}

/** MS-DOS date and time, which is what the format stores. Two-second resolution. */
function dosStamp(date: Date): { dosTime: number; dosDate: number } {
  /* The epoch is 1980; anything earlier clamps rather than wrapping to garbage. */
  const year = Math.max(1980, date.getFullYear());
  return {
    dosTime:
      (date.getHours() << 11) |
      (date.getMinutes() << 5) |
      (date.getSeconds() >> 1),
    dosDate: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

/** Collects the archive in memory. Fine for a handful; not for a whole library. */
export function memorySink(): ZipSink & { blob(): Blob } {
  const chunks: BlobPart[] = [];
  return {
    write(chunk) {
      /* Copied: the caller may reuse its buffer, and a Blob keeps a reference. */
      chunks.push(new Uint8Array(chunk).buffer as ArrayBuffer);
      return Promise.resolve();
    },
    close() {
      return Promise.resolve();
    },
    blob() {
      return new Blob(chunks, { type: "application/zip" });
    },
  };
}
